/**
 * Productivity Table Component
 *
 * Displays radiologist productivity metrics in a table
 */

import { Loader2, Users, TrendingUp } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import { Badge } from '@/presentation/components/ui/Badge'
import { useRadiologistProductivity } from '@/application/dashboard'

/**
 * Format hours to a readable string
 */
function formatHours(hours: number | null): string {
  if (hours === null) return '-'
  if (hours < 1) {
    return `${Math.round(hours * 60)} min`
  }
  return `${hours.toFixed(1)}h`
}

export function ProductivityTable() {
  const { data, isLoading, isError } = useRadiologistProductivity()

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5" />
            Productividad de Radiólogos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[200px]">
            <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" />
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isError || !data) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5" />
            Productividad de Radiólogos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[200px] text-[var(--foreground-muted)]">
            Error al cargar datos
          </div>
        </CardContent>
      </Card>
    )
  }

  const { productivity } = data

  if (productivity.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Users className="h-5 w-5" />
            Productividad de Radiólogos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[200px] text-[var(--foreground-muted)]">
            No hay radiólogos activos
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <TrendingUp className="h-5 w-5" />
          Productividad de Radiólogos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Radiólogo</TableHead>
              <TableHead className="text-center">Hoy</TableHead>
              <TableHead className="text-center">Semana</TableHead>
              <TableHead className="text-center">Mes</TableHead>
              <TableHead className="text-right">TAT Prom.</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {productivity.map((item, index) => (
              <TableRow key={item.radiologistId}>
                <TableCell>
                  <div className="flex items-center gap-2">
                    {index === 0 && productivity.length > 1 && (
                      <Badge variant="default" className="text-xs">
                        #1
                      </Badge>
                    )}
                    <div>
                      <p className="font-medium text-sm">{item.radiologistName}</p>
                      <p className="text-xs text-[var(--foreground-muted)]">
                        {item.radiologistEmail}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <Badge
                    variant={item.completedToday > 0 ? 'default' : 'secondary'}
                  >
                    {item.completedToday}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  <span className="font-medium">{item.completedWeek}</span>
                </TableCell>
                <TableCell className="text-center">
                  <span className="font-medium">{item.completedMonth}</span>
                </TableCell>
                <TableCell className="text-right">
                  <span
                    className={
                      item.avgTatHours !== null && item.avgTatHours < 4
                        ? 'text-green-600 dark:text-green-400'
                        : item.avgTatHours !== null && item.avgTatHours > 8
                        ? 'text-red-600 dark:text-red-400'
                        : ''
                    }
                  >
                    {formatHours(item.avgTatHours)}
                  </span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
